<template>
  <div class="annotations-panel" :class="{ collapsed: isCollapsed }">
    <AnnotationEditModal
      :is-open="editModalOpen"
      :annotation="annotationToEdit"
      @close="handleCloseEditModal"
      @save="handleSaveAnnotation"
    />
    <ConfirmModal
      :show="deleteConfirmOpen"
      title="Supprimer l'annotation"
      :message="deleteConfirmMessage"
      confirm-label="Supprimer"
      :danger="true"
      @confirm="confirmDeleteAnnotation"
      @cancel="deleteConfirmOpen = false; annotationToDelete = null"
    />

    <div class="panel-header">
      <h2>Annotations</h2>
      <div class="header-actions">
        <button 
          v-if="annotations.length > 0" 
          @click="handleToggleAllAnnotations" 
          class="btn-icon-header" 
          :title="allAnnotationsVisible ? 'Masquer toutes les annotations' : 'Afficher toutes les annotations'"
        >
          <span class="material-symbols-outlined">{{ allAnnotationsVisible ? 'visibility' : 'visibility_off' }}</span>
        </button>
        <button @click="isCollapsed = !isCollapsed" class="btn-collapse" :title="isCollapsed ? 'Afficher' : 'Masquer'">
          <span class="material-symbols-outlined">{{ isCollapsed ? 'chevron_left' : 'chevron_right' }}</span>
        </button>
        <button v-if="!isCollapsed" @click="showToolbar = !showToolbar" class="btn-toggle" :title="showToolbar ? 'Masquer les outils' : 'Afficher les outils'">
          {{ showToolbar ? '−' : '+' }}
        </button>
      </div>
    </div>

    <div v-if="!isCollapsed" class="panel-content">
      <div v-if="showToolbar" class="toolbar">
        <button
          v-for="tool in tools"
          :key="tool.type"
          @click="handleToolSelect(tool.type)"
          :class="['tool-btn', { active: activeTool === tool.type }]"
          :title="tool.description"
        >
          <span class="material-symbols-outlined tool-icon">{{ tool.icon }}</span>
          <span class="tool-label">{{ tool.label }}</span>
        </button>
      </div>

      <div class="annotations-list">
      <div v-if="loading" class="loading">Chargement...</div>
      <div v-else-if="error" class="error">{{ error }}</div>
      <div v-else-if="!currentHypothesis" class="empty-state">
        Sélectionnez une hypothèse pour voir les annotations
      </div>
      <div v-else-if="annotations.length === 0" class="empty-state">
        <p>Aucune annotation</p>
        <p class="hint">Utilisez les outils ci-dessus pour créer des annotations</p>
      </div>
      <div
        v-for="annotation in annotations"
        :key="annotation.id"
        class="annotation-item"
        :class="{ selected: selectedAnnotation?.id === annotation.id }"
        @click="handleSelectAnnotation(annotation)"
      >
        <div class="annotation-header">
          <span class="annotation-type" :style="{ color: annotation.color || undefined }">
            {{ annotation.name || getTypeLabel(annotation.type) }}
          </span>
          <div class="annotation-actions">
            <button 
              @click.stop="handleToggleVisibility(annotation)" 
              class="btn-icon" 
              :title="isAnnotationVisible(annotation) ? 'Masquer' : 'Afficher'"
            >
              <span class="material-symbols-outlined">{{ isAnnotationVisible(annotation) ? 'visibility' : 'visibility_off' }}</span>
            </button>
            <button @click.stop="handleEditAnnotation(annotation)" class="btn-icon" title="Éditer">
              <span class="material-symbols-outlined">edit</span>
            </button>
            <button @click.stop="handleDeleteAnnotation(annotation)" class="btn-icon" title="Supprimer">
              <span class="material-symbols-outlined">delete</span>
            </button>
          </div>
        </div>
        <div v-if="annotation.description" class="annotation-description">
          {{ annotation.description }}
        </div>
        <div class="annotation-preview">
          {{ formatAnnotationPreview(annotation) }}
        </div>
        <div class="annotation-date">
          {{ formatDate(annotation.created_at) }}
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useHypothesesStore } from '@stores/hypotheses.store';
import { annotationsApi, type Annotation, type AnnotationType } from '@api/annotations.api';
import { useToast } from '../composables/useToast';
import { REFERENCE_SCALE_PX_PER_MM } from '@/constants/canvas';
import AnnotationEditModal from './AnnotationEditModal.vue';
import ConfirmModal from './ConfirmModal.vue';

const hypothesesStore = useHypothesesStore();
const { success, error: showError } = useToast();

const showToolbar = ref(true);
const isCollapsed = ref(true);
const activeTool = ref<AnnotationType | null>(null);
const selectedAnnotation = ref<Annotation | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);
const editModalOpen = ref(false);
const annotationToEdit = ref<Annotation | null>(null);
const annotationsHidden = ref(false);
const deleteConfirmOpen = ref(false);
const annotationToDelete = ref<Annotation | null>(null);

const deleteConfirmMessage = computed(() =>
  annotationToDelete.value
    ? `Supprimer l'annotation "${getTypeLabel(annotationToDelete.value.type)}" ?`
    : ''
);

const tools = [
  { type: 'label' as AnnotationType, label: 'Étiquette', icon: 'label', description: 'Ajouter une étiquette textuelle' },
  { type: 'line' as AnnotationType, label: 'Ligne', icon: 'show_chart', description: 'Dessiner une ligne' },
  { type: 'rectangle' as AnnotationType, label: 'Rectangle', icon: 'crop_square', description: 'Dessiner un rectangle' },
  { type: 'circle' as AnnotationType, label: 'Cercle', icon: 'circle', description: 'Dessiner un cercle' },
  { type: 'measure' as AnnotationType, label: 'Mesure', icon: 'straighten', description: 'Mesurer une distance' },
];

const currentHypothesis = computed(() => hypothesesStore.currentHypothesis);
const annotations = computed(() => currentHypothesis.value?.annotations || []);

// État de visibilité locale (ne modifie pas la BDD)
const allAnnotationsVisible = computed(() => !annotationsHidden.value);

// Conversion pixels -> millimètres (même échelle de référence que le canvas)
const pxToMm = (px: number): number => px / REFERENCE_SCALE_PX_PER_MM;

const handleToolSelect = (toolType: AnnotationType) => {
  if (activeTool.value === toolType) {
    // Désactiver l'outil si on clique dessus à nouveau
    activeTool.value = null;
  } else {
    activeTool.value = toolType;
  }
  
  // Déclencher un événement pour activer le mode annotation sur le canvas
  window.dispatchEvent(
    new CustomEvent('annotation-tool-selected', {
      detail: { tool: activeTool.value },
    })
  );
};

const handleSelectAnnotation = (annotation: Annotation) => {
  if (selectedAnnotation.value?.id === annotation.id) return;
  selectedAnnotation.value = annotation;
  // Déclencher un événement pour mettre en évidence l'annotation sur le canvas
  window.dispatchEvent(
    new CustomEvent('annotation-selected', {
      detail: { annotation },
    })
  );
};

const handleExternalAnnotationSelected = (event: Event) => {
  const customEvent = event as CustomEvent<{ annotation?: Annotation }>;
  const annotationId = customEvent.detail?.annotation?.id;
  if (typeof annotationId !== 'number') return;
  if (selectedAnnotation.value?.id === annotationId) return;
  const matchedAnnotation = annotations.value.find((annotation) => annotation.id === annotationId);
  if (!matchedAnnotation) return;
  selectedAnnotation.value = matchedAnnotation;
};

const handleEditAnnotation = (annotation: Annotation) => {
  annotationToEdit.value = annotation;
  editModalOpen.value = true;
};

const handleCloseEditModal = () => {
  editModalOpen.value = false;
  annotationToEdit.value = null;
};

const handleSaveAnnotation = async (data: { name?: string | null; description?: string | null; color?: string | null; strokeWidth?: number }) => {
  if (!annotationToEdit.value) return;

  loading.value = true;
  error.value = null;
  try {
    await annotationsApi.update(annotationToEdit.value.id, {
      name: data.name,
      description: data.description,
      color: data.color,
      stroke_width: data.strokeWidth,
    });
    success('Annotation mise à jour');
    
    // Recharger l'hypothèse pour mettre à jour la liste
    if (currentHypothesis.value) {
      await hypothesesStore.loadHypothesis(currentHypothesis.value.id);
    }
    
    handleCloseEditModal();
  } catch (err: any) {
    const message = err.response?.data?.message || err.message || 'Erreur lors de la mise à jour';
    error.value = message;
    showError(message);
  } finally {
    loading.value = false;
  }
};

const isAnnotationVisible = (a: { visible?: boolean | number }): boolean =>
  a.visible !== false && a.visible !== 0;

const handleToggleVisibility = async (annotation: Annotation) => {
  loading.value = true;
  error.value = null;
  try {
    const newVisible = !isAnnotationVisible(annotation);
    await annotationsApi.update(annotation.id, { visible: newVisible });
    if (currentHypothesis.value) {
      await hypothesesStore.loadHypothesis(currentHypothesis.value.id);
    }
    success(newVisible ? 'Annotation affichée' : 'Annotation masquée');
  } catch (err: any) {
    const message = err.response?.data?.message || err.message || 'Erreur lors de la mise à jour';
    error.value = message;
    showError(message);
  } finally {
    loading.value = false;
  }
};

const handleToggleAllAnnotations = () => {
  if (annotations.value.length === 0) return;
  
  // Basculer l'état local uniquement (pas de modification en BDD)
  annotationsHidden.value = !annotationsHidden.value;
  
  // Émettre un événement pour que le canvas se mette à jour
  window.dispatchEvent(
    new CustomEvent('annotations-visibility-changed', {
      detail: { hidden: annotationsHidden.value },
    })
  );
};

const handleDeleteAnnotation = (annotation: Annotation) => {
  annotationToDelete.value = annotation;
  deleteConfirmOpen.value = true;
};

const confirmDeleteAnnotation = async () => {
  if (!annotationToDelete.value) return;

  const annotation = annotationToDelete.value;
  deleteConfirmOpen.value = false;
  annotationToDelete.value = null;

  loading.value = true;
  error.value = null;
  try {
    await annotationsApi.delete(annotation.id);
    success('Annotation supprimée');
    if (currentHypothesis.value) {
      await hypothesesStore.loadHypothesis(currentHypothesis.value.id);
    }
  } catch (err: any) {
    const message = err.response?.data?.message || err.message || 'Erreur lors de la suppression';
    error.value = message;
    showError(message);
  } finally {
    loading.value = false;
  }
};

const getTypeLabel = (type: AnnotationType): string => {
  const tool = tools.find((t) => t.type === type);
  return tool?.label || type;
};

const formatAnnotationPreview = (annotation: Annotation): string => {
  const data = typeof annotation.data === 'string' 
    ? JSON.parse(annotation.data) 
    : annotation.data;

  switch (annotation.type) {
    case 'label':
      return data.text || 'Étiquette sans texte';
    case 'line': {
      const dx = data.x2 - data.x1;
      const dy = data.y2 - data.y1;
      const lengthPx = Math.sqrt(dx * dx + dy * dy);
      const lengthMm = pxToMm(lengthPx);
      return lengthMm > 0 
        ? `Ligne: ${lengthMm.toFixed(1)} mm`
        : `Ligne de (${data.x1?.toFixed(0)}, ${data.y1?.toFixed(0)}) à (${data.x2?.toFixed(0)}, ${data.y2?.toFixed(0)})`;
    }
    case 'rectangle': {
      const width = Math.abs(data.x2 - data.x1);
      const height = Math.abs(data.y2 - data.y1);
      const widthMm = pxToMm(width);
      const heightMm = pxToMm(height);
      return widthMm > 0 && heightMm > 0
        ? `Rectangle ${widthMm.toFixed(1)}×${heightMm.toFixed(1)} mm`
        : `Rectangle ${width.toFixed(0)}×${height.toFixed(0)}`;
    }
    case 'circle': {
      const radiusMm = pxToMm(data.radius);
      const diameterMm = radiusMm * 2;
      return radiusMm > 0
        ? `Cercle ⌀${diameterMm.toFixed(1)} mm`
        : `Cercle r=${data.radius?.toFixed(0)}`;
    }
    case 'measure': {
      // Calculer la distance à partir des coordonnées
      const dx = data.x2 - data.x1;
      const dy = data.y2 - data.y1;
      const lengthPx = Math.sqrt(dx * dx + dy * dy);
      const lengthMm = pxToMm(lengthPx);
      return lengthMm > 0
        ? `Mesure: ${lengthMm.toFixed(1)} mm`
        : `Mesure: ${lengthPx.toFixed(0)} px`;
    }
    default:
      return JSON.stringify(data);
  }
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

onMounted(() => {
  window.addEventListener('annotation-selected', handleExternalAnnotationSelected as EventListener);
});

onUnmounted(() => {
  window.removeEventListener('annotation-selected', handleExternalAnnotationSelected as EventListener);
});

// Réinitialiser l'outil actif quand on change d'hypothèse
watch(
  () => currentHypothesis.value?.id,
  () => {
    activeTool.value = null;
    selectedAnnotation.value = null;
  }
);
</script>

<style scoped>
.annotations-panel {
  width: 250px;
  min-width: 250px;
  height: 100%;
  background-color: #2a2a2a;
  border-left: 1px solid #444;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: height 0.3s ease, width 0.3s ease, min-width 0.3s ease;
}

.annotations-panel.collapsed {
  width: 48px;
  min-width: 48px;
  height: 100%;
  flex-direction: row;
  position: relative;
}

.panel-header {
  padding: 1rem;
  border-bottom: 1px solid #444;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.annotations-panel.collapsed .panel-header {
  border-bottom: none;
  border-left: 1px solid #444;
  padding: 1rem 0.5rem;
  padding-top: 0;
  flex-direction: column;
  justify-content: flex-start;
  gap: 1rem;
  height: calc(100% - 60px);
  margin-top: 60px;
  cursor: pointer;
}

.annotations-panel.collapsed .panel-header h2 {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
  white-space: nowrap;
  margin: 0;
  margin-top: 3rem;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.annotations-panel.collapsed .header-actions {
  position: absolute;
  top: 0.5rem;
  left: 50%;
  transform: translateX(-50%);
  flex-direction: column;
  gap: 0.25rem;
}

.btn-collapse {
  background: #333;
  border: none;
  color: #e0e0e0;
  cursor: pointer;
  font-size: 1rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: all 0.2s;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-collapse .material-icons,
.btn-collapse .material-symbols-outlined {
  font-size: 20px;
}

.btn-collapse:hover {
  background-color: #444;
  color: #fff;
}

.btn-icon-header {
  background: #333;
  border: none;
  color: #e0e0e0;
  cursor: pointer;
  font-size: 1rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: all 0.2s;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-icon-header .material-symbols-outlined {
  font-size: 20px;
}

.btn-icon-header:hover {
  background-color: #444;
  color: #fff;
}

.annotations-panel.collapsed .btn-toggle {
  display: none;
}

.panel-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: max-height 0.3s ease, opacity 0.2s ease;
  max-height: 1000px;
  opacity: 1;
}

.annotations-panel.collapsed .panel-content {
  display: none;
}

.panel-header h2 {
  margin: 0;
  font-size: 1.2rem;
  color: #e0e0e0;
}

.btn-toggle {
  background: none;
  border: none;
  color: #999;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-toggle:hover {
  color: #e0e0e0;
}

.toolbar {
  padding: 0.5rem;
  border-bottom: 1px solid #444;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tool-btn {
  padding: 0.5rem;
  background-color: #333;
  border: 1px solid #444;
  border-radius: 4px;
  color: #e0e0e0;
  cursor: pointer;
  font-size: 0.85rem;
  flex: 1;
  min-width: 80px;
}

.tool-btn:hover {
  background-color: #3a3a3a;
}

.tool-btn.active {
  background-color: #4a9eff;
  border-color: #4a9eff;
}

.annotations-list {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
}

.empty-state {
  padding: 2rem;
  text-align: center;
  color: #999;
  font-size: 0.9rem;
}

.annotation-item {
  padding: 0.75rem;
  background-color: #333;
  border: 1px solid #444;
  border-radius: 4px;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  color: #e0e0e0;
  cursor: pointer;
  transition: all 0.2s;
}

.annotation-item:hover {
  background-color: #3a3a3a;
  border-color: #555;
}

.annotation-item.selected {
  background-color: #2a4a6a;
  border-color: #4a9eff;
}

.annotation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.annotation-type {
  font-weight: 600;
  color: #4a9eff;
  font-size: 0.9rem;
}

.annotation-actions {
  display: flex;
  gap: 0.25rem;
}

.btn-icon {
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  font-size: 1rem;
  padding: 0.25rem 0.5rem;
  border-radius: 3px;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-icon .material-symbols-outlined {
  font-size: 18px;
}

.btn-icon:hover {
  background-color: #444;
  color: #e0e0e0;
}

.annotation-description {
  color: #aaa;
  font-size: 0.8rem;
  font-style: italic;
  margin-bottom: 0.5rem;
  line-height: 1.3;
}

.annotation-preview {
  color: #ccc;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
}

.annotation-date {
  color: #666;
  font-size: 0.75rem;
}

.tool-icon {
  font-size: 18px;
  margin-right: 0.25rem;
}

.tool-label {
  font-size: 0.85rem;
}

.loading,
.error {
  padding: 1rem;
  text-align: center;
  color: #e0e0e0;
}

.error {
  color: #ff6b6b;
}

.hint {
  font-size: 0.8rem;
  color: #666;
  margin-top: 0.5rem;
}
</style>
